import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import {
  Container,
  SectionHeader,
  PullQuote,
  CapabilityList,
  ImageBlock,
  CTAStrip,
} from "@/components/Sections";
import {
  IMAGES,
  AUDIENCES,
  OPPORTUNITY_LIST,
  RANGER_CAPABILITIES,
  LEADERSHIP_LIST,
  PATHWAYS,
  LEARNING_ACTIVITIES,
  FUTURE_ROLES,
} from "@/lib/content";

export default function Home() {
  return (
    <div data-testid="page-home" className="bg-[#F6F1E6] text-[#1F2A1B]">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100vh] flex items-end pt-32 pb-20"
      >
        <div className="absolute inset-0 warm-overlay">
          <img
            src={IMAGES.hero}
            alt="Australian outback landscape with red soil and scattered bushes"
            className="w-full h-full object-cover warm-tint"
          />
        </div>
        <Container className="relative z-10">
          <div className="max-w-5xl">
            <p className="overline mb-8 fade-up" data-testid="hero-overline">
              Carbon on Country
            </p>
            <h1
              data-testid="hero-title"
              className="display-xxl text-[44px] sm:text-6xl md:text-7xl lg:text-[112px] mb-10 fade-up text-[#F6F1E6]"
              style={{ animationDelay: "0.1s" }}
            >
              Building Indigenous Leadership
              <br />
              for{" "}
              <span className="italic text-[#E0B66B]">Environmental Stewardship.</span>
            </h1>
            <p
              className="text-[#E5DFD0] text-base md:text-xl leading-relaxed max-w-2xl mb-10 fade-up"
              style={{ animationDelay: "0.2s" }}
            >
              Carbon markets, Nature Repair projects, biodiversity investment and
              landscape restoration all rely on one thing: people who can measure,
              monitor and verify environmental outcomes.
            </p>
            <div
              className="flex flex-wrap gap-2 mb-12 fade-up"
              style={{ animationDelay: "0.3s" }}
            >
              {AUDIENCES.map((a) => (
                <span
                  key={a}
                  className="text-[10px] md:text-xs tracking-[0.24em] uppercase text-[#F6F1E6] border border-[#F6F1E6]/40 px-3 py-2"
                >
                  {a}
                </span>
              ))}
            </div>
            <div
              className="flex flex-col sm:flex-row gap-5 fade-up"
              style={{ animationDelay: "0.4s" }}
            >
              <Link
                to="/pathways"
                data-testid="hero-cta-pathways"
                className="btn-primary"
              >
                Explore Training Pathways
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="btn-ghost"
              >
                Start a conversation
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* COUNTRY NEEDS MORE THAN PROJECTS */}
      <section
        data-testid="needs-more-section"
        className="py-32 md:py-48 border-t border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-7">
              <SectionHeader
                label="Premise"
                title="Country needs more"
                italic="than projects."
              />
              <div className="space-y-6 text-[#4F5A48] text-lg leading-relaxed max-w-2xl">
                <p>
                  Across Australia, investment in carbon markets, biodiversity
                  conservation, restoration and natural capital is growing rapidly.
                </p>
                <p>
                  Governments, industry and investors are seeking measurable
                  environmental outcomes. New projects are being developed. New
                  markets are emerging. New reporting requirements are being
                  introduced.
                </p>
                <p>
                  Yet one challenge receives far less attention than carbon
                  methodologies, biodiversity metrics or project finance.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 lg:pt-20">
              <ul className="space-y-8">
                {[
                  "Who will do the work?",
                  "Who will monitor outcomes on the ground?",
                  "Who will verify environmental claims?",
                  "Who will manage environmental data over decades rather than years?",
                  "Who will ensure environmental investment translates into long-term capability within communities?",
                ].map((q, i) => (
                  <li
                    key={i}
                    className="flex items-baseline gap-5 border-b border-[#1F2A1B]/10 pb-6"
                  >
                    <span className="section-number">0{i + 1}</span>
                    <p className="font-serif text-2xl md:text-3xl leading-[1.15] text-[#1F2A1B]">
                      {q}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <PullQuote testid="quote-people">
            We believe the future restoration economy requires more than
            projects. It requires people.
          </PullQuote>
        </Container>
      </section>

      {/* THE OPPORTUNITY */}
      <section
        data-testid="opportunity-section"
        className="py-32 md:py-40 border-t border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <SectionHeader
                number="Section 01"
                label="The opportunity"
                title="A new era of"
                italic="environmental investment."
              />
              <ImageBlock
                src={IMAGES.opportunity}
                alt="Brown dirt road between green grass field"
                caption="The Opportunity"
                className="aspect-[4/3] mt-4"
                testid="opportunity-image"
              />
            </div>
            <div className="lg:col-span-7">
              <p className="text-[#4F5A48] text-lg leading-relaxed mb-8 max-w-2xl">
                Carbon projects, biodiversity markets, Nature Repair projects,
                mine rehabilitation, restoration initiatives and natural capital
                reporting are creating demand for a new workforce.
              </p>
              <p className="overline mb-4">These projects require people who can</p>
              <CapabilityList
                items={OPPORTUNITY_LIST}
                columns={2}
                testid="opportunity-list"
              />
              <p className="text-[#1F2A1B] font-serif italic text-2xl md:text-3xl mt-12 leading-snug max-w-2xl">
                Indigenous ranger programs already undertake many of these
                activities every day.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* A WORKFORCE GAP IS EMERGING */}
      <section
        data-testid="workforce-gap-section"
        className="relative py-32 md:py-48"
      >
        <div className="absolute inset-0 opacity-30">
          <img
            src={IMAGES.workforce}
            alt="Vast rugged outback with dirt road to horizon"
            className="w-full h-full object-cover warm-tint"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F6F1E6] via-[#F6F1E6]/80 to-[#F6F1E6]" />
        </div>
        <Container className="relative z-10">
          <div className="max-w-4xl">
            <SectionHeader
              label="Workforce"
              title="A workforce gap"
              italic="is emerging."
            />
            <div className="space-y-6 text-[#4F5A48] text-lg leading-relaxed">
              <p>
                Much of the discussion around carbon and biodiversity markets
                focuses on project development. Less attention is given to the
                workforce required to support these projects over the long term.
              </p>
              <ul className="space-y-4 pl-0 mt-10">
                {[
                  "Carbon projects require monitoring, reporting and verification.",
                  "Nature Repair projects require biodiversity assessment and ongoing evaluation.",
                  "Restoration projects require environmental monitoring and data collection.",
                  "Natural capital reporting requires evidence, measurement and accountability.",
                ].map((line, i) => (
                  <li key={i} className="flex gap-5">
                    <span className="text-[#C45A3C] font-serif">—</span>
                    <span className="text-[#1F2A1B] text-base md:text-lg">{line}</span>
                  </li>
                ))}
              </ul>
              <p className="font-serif italic text-2xl md:text-3xl text-[#3F5132] pt-8">
                Indigenous ranger programs represent one of Australia&apos;s most
                experienced environmental workforces.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* WHY INDIGENOUS RANGERS */}
      <section
        data-testid="why-rangers-section"
        className="py-32 md:py-40 border-t border-[#1F2A1B]/10"
      >
        <Container>
          <SectionHeader
            number="Section 02"
            label="Why Indigenous Rangers"
            title="Rangers already do"
            italic="this work."
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="text-[#4F5A48] text-lg leading-relaxed">
                Indigenous rangers already possess many of the skills required by
                carbon, biodiversity and natural capital projects.
              </p>
              <p className="font-serif italic text-3xl text-[#3F5132] mt-10 leading-snug">
                The next step is creating pathways that translate these existing
                capabilities into recognised environmental professions.
              </p>
            </div>
            <div className="lg:col-span-7">
              <CapabilityList items={RANGER_CAPABILITIES} testid="ranger-capabilities" />
            </div>
          </div>

          {/* Knowledge Meets Technology insert */}
          <div className="mt-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-5">
              <ImageBlock
                src={IMAGES.data}
                alt="Environmental data systems"
                caption="Knowledge × Technology"
                className="aspect-[3/4]"
                testid="technology-image"
              />
            </div>
            <div className="lg:col-span-7">
              <p className="overline mb-5">Knowledge meets technology</p>
              <h3 className="font-serif text-3xl md:text-5xl leading-[1.05] text-[#1F2A1B] mb-8">
                The future workforce will require both
                <span className="italic text-[#3F5132]"> cultural knowledge</span> and
                technical capability.
              </h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  "Satellite imagery",
                  "GIS mapping",
                  "Drone technology",
                  "Environmental data systems",
                  "Biodiversity monitoring",
                  "Carbon accounting",
                ].map((t) => (
                  <span
                    key={t}
                    className="text-xs tracking-[0.22em] uppercase text-[#3F5132] border border-[#3F5132]/40 px-3 py-2"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <p className="text-[#4F5A48] text-lg leading-relaxed max-w-2xl">
                When combined with local knowledge, long-term observation and
                cultural understanding of landscape change, they create powerful
                opportunities for better environmental outcomes.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* BEYOND CONSULTATION + FROM PARTICIPATION TO LEADERSHIP */}
      <section
        data-testid="leadership-section"
        className="py-32 md:py-40 border-t border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-6">
              <p className="overline mb-5">Beyond consultation</p>
              <h3 className="font-serif text-3xl md:text-5xl leading-[1.05] mb-8 text-[#1F2A1B]">
                Participation alone does not build
                <span className="italic text-[#3F5132]"> long-term capability.</span>
              </h3>
              <p className="text-[#4F5A48] text-lg leading-relaxed">
                Long-term capability is built through training, experience,
                qualifications, employment pathways and leadership opportunities.
              </p>
            </div>
            <div className="lg:col-span-6 lg:border-l lg:border-[#1F2A1B]/10 lg:pl-12">
              <SectionHeader
                number="Section 03"
                label="From participation to leadership"
                title="Indigenous"
                italic="leadership."
              />
              <p className="overline mb-4">Leadership means building capability to</p>
              <CapabilityList items={LEADERSHIP_LIST} columns={2} testid="leadership-list" />
            </div>
          </div>

          <PullQuote testid="quote-participants">
            The future of environmental markets should include Indigenous people
            not only as participants, but as practitioners, managers and leaders.
          </PullQuote>
        </Container>
      </section>

      {/* TRAINING PATHWAYS PREVIEW */}
      <section
        data-testid="pathways-preview-section"
        className="py-32 md:py-40 border-t border-[#1F2A1B]/10 bg-[#EBE3D0]"
      >
        <Container>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16">
            <SectionHeader
              number="Section 04"
              label="Training Pathways"
              title="Six emerging"
              italic="environmental professions."
              className="mb-0"
            />
            <Link
              to="/pathways"
              data-testid="pathways-view-all"
              className="btn-ghost shrink-0"
            >
              View all pathways
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2A1B]/10">
            {PATHWAYS.map((p) => (
              <Link
                key={p.slug}
                to={p.detailHref || `/pathways#${p.slug}`}
                data-testid={`pathway-card-${p.slug}`}
                className="group bg-[#F6F1E6] p-8 md:p-10 hover:bg-[#ECE4D2] transition-colors duration-500 flex flex-col"
              >
                <div className="flex items-start justify-between mb-12">
                  <span className="section-number">{p.number}</span>
                  <ArrowUpRight
                    size={20}
                    className="text-[#8A8775] group-hover:text-[#C45A3C] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                  />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight text-[#1F2A1B] mb-5 group-hover:text-[#3F5132] transition-colors">
                  {p.title}
                </h3>
                <p className="text-[#4F5A48] text-base leading-relaxed flex-1">
                  {p.summary}
                </p>
                <div className="mt-8 pt-6 border-t border-[#1F2A1B]/10">
                  <p className="overline mb-2">Career outcomes</p>
                  <p className="text-[#3F5132] text-sm">
                    {p.careers.slice(0, 2).join(" · ")}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* SEED TRAINING FEATURE — between Pathways preview and Learning on Country */}
      <section
        data-testid="seed-feature-section"
        className="py-32 md:py-40 border-t border-[#1F2A1B]/10 bg-[#EBE3D0]"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-6">
              <ImageBlock
                src={IMAGES.regrowth}
                alt="Native regrowth on Country — seed and restoration"
                caption="Native Seed × Restoration"
                className="aspect-[4/5]"
                testid="seed-feature-image"
              />
            </div>
            <div className="lg:col-span-6">
              <p className="overline mb-5">New Pathway · Native Seed & Restoration</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-[64px] leading-[1.02] text-[#1F2A1B] mb-8">
                Restoration begins
                <br />
                with <span className="italic text-[#3F5132]">seed.</span>
              </h2>
              <p className="text-[#4F5A48] text-lg leading-relaxed mb-6 max-w-xl">
                Carbon projects, mine rehabilitation, Nature Repair and
                biodiversity conservation all rely on high-quality native seed —
                collected from the right species, in the right place, at the
                right time.
              </p>
              <p className="text-[#4F5A48] text-lg leading-relaxed mb-10 max-w-xl">
                A seven-module pathway introducing Indigenous rangers and
                environmental practitioners to the foundations of native seed
                collection, provenance, storage and restoration planning.
              </p>
              <div className="flex flex-wrap gap-2 mb-10">
                {[
                  "Seed systems",
                  "Collection planning",
                  "Provenance",
                  "Field techniques",
                  "Storage",
                  "Data & monitoring",
                  "Restoration readiness",
                ].map((t) => (
                  <span
                    key={t}
                    className="text-xs tracking-[0.22em] uppercase text-[#3F5132] border border-[#3F5132]/40 px-3 py-2"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <Link
                to="/pathways/native-seed-collection"
                data-testid="seed-feature-cta"
                className="btn-primary"
              >
                Explore seed training
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* LEARNING ON COUNTRY */}
      <section
        data-testid="learning-section"
        className="py-32 md:py-40 border-t border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-7">
              <SectionHeader
                number="Section 05"
                label="Learning on Country"
                title="Technical training"
                italic="meets place-based learning."
              />
              <p className="text-[#4F5A48] text-lg leading-relaxed mb-8 max-w-2xl">
                Environmental capability is strongest when technical skills are
                developed alongside local knowledge, cultural governance and
                practical experience.
              </p>
              <CapabilityList items={LEARNING_ACTIVITIES} testid="learning-activities" />
            </div>
            <div className="lg:col-span-5 lg:pt-32">
              <ImageBlock
                src={IMAGES.fire}
                alt="Firefighter controlling bushfire — fire management"
                caption="Field-based learning"
                className="aspect-[3/4]"
                testid="learning-image"
              />
              <p className="font-serif italic text-xl md:text-2xl text-[#3F5132] mt-10 leading-snug">
                Many of the skills required for carbon, biodiversity and natural
                capital projects are best learned through practical application.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* FUTURE WORKFORCE AREAS */}
      <section
        data-testid="future-workforce-section"
        className="py-32 md:py-40 border-t border-[#1F2A1B]/10 bg-[#EBE3D0]"
      >
        <Container>
          <SectionHeader
            number="Section 07"
            label="Future workforce areas"
            title="A generation of"
            italic="new professions."
          />
          <p className="text-[#4F5A48] text-lg leading-relaxed mb-12 max-w-3xl">
            A decade ago, many of these roles did not exist. The restoration
            economy is expanding rapidly — and so are the careers within it.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
            {FUTURE_ROLES.map((r, i) => (
              <div
                key={r}
                className="flex items-baseline gap-5 border-b border-[#1F2A1B]/10 py-5 group hover:border-[#C45A3C]/60 transition-colors"
              >
                <span className="font-serif italic text-[#C45A3C] text-sm w-8 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[#1F2A1B] text-base md:text-lg">{r}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <CTAStrip
        heading="Country needs people who can measure change."
        body="Whether you lead a ranger program, a Traditional Owner organisation or an Aboriginal corporation — Carbon on Country helps build the workforce pathways that translate existing capability into recognised environmental professions."
        label="Work with us"
        testid="home-cta-strip"
      />
    </div>
  );
}
